/**
 * WFCSolver — 2D Wave Function Collapse solver.
 *
 * Algorithm:
 *   1. Each cell starts with all tiles possible (superposition).
 *   2. Find the uncollapsed cell with the lowest Shannon entropy.
 *   3. Collapse it: pick a tile weighted by tile.weight, seeded deterministically.
 *   4. Propagate constraints via BFS — remove tiles from neighbours that are no
 *      longer compatible given the adjacency rules.
 *   5. Repeat until all cells are collapsed or a contradiction is hit.
 *   6. On contradiction: backtrack to the last collapse point and try a different tile.
 *
 * Adjacency rules come from tileset.json (edges: N/E/S/W lists of allowed neighbour ids).
 */

import type { TileDefinition, TilesetConfig } from './TilesetLoader';

/** A collapsed cell result. */
export interface WFCResult {
  width: number;
  height: number;
  /** tiles[row][col] = tile index (1-based, matching the sprite sheet column). */
  tiles: number[][];
}

/** Simple seeded PRNG (Mulberry32). */
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return (): number => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Direction = 'N' | 'E' | 'S' | 'W';
const OPPOSITE: Record<Direction, Direction> = { N: 'S', E: 'W', S: 'N', W: 'E' };
const DIR_DELTA: Record<Direction, [number, number]> = {
  N: [-1, 0],
  E: [0, 1],
  S: [1, 0],
  W: [0, -1],
};

/** Precomputed compatibility: for each tile id, which tile ids are allowed in each direction. */
type CompatibilityMap = Map<string, Map<Direction, Set<string>>>;

/** Snapshot for backtracking. */
interface Snapshot {
  row: number;
  col: number;
  /** Deep-copied possible sets for all cells. */
  possible: Set<string>[][];
  /** The tile id that was chosen (and led to this snapshot). */
  chosen: string;
  /** Remaining candidates at (row, col) after removing the chosen one. */
  remaining: string[];
}

export class WFCSolver {
  private tileDefs: Map<string, TileDefinition>;
  private compat: CompatibilityMap;
  private allIds: string[];

  constructor(tileset: TilesetConfig) {
    this.tileDefs = new Map(tileset.tiles.map((t) => [t.id, t]));
    this.allIds = tileset.tiles.map((t) => t.id);
    this.compat = this.buildCompatibility(tileset);
  }

  /**
   * Run WFC on a grid of the given dimensions.
   * @param width  Grid width in tiles.
   * @param height Grid height in tiles.
   * @param seed   Deterministic seed.
   * @param constraints Optional forced tiles: array of {row, col, tileId}.
   */
  solve(
    width: number,
    height: number,
    seed: number,
    constraints: { row: number; col: number; tileId: string }[] = []
  ): WFCResult {
    const rng = makeRng(seed);

    // Initialise each cell with all tile ids possible
    let possible: Set<string>[][] = Array.from({ length: height }, () =>
      Array.from({ length: width }, () => new Set(this.allIds))
    );

    // Apply hard constraints (border cells forced to 'wall', spawn zones forced to 'grass', etc.)
    for (const { row, col, tileId } of constraints) {
      if (row < 0 || row >= height || col < 0 || col >= width) continue;
      possible[row][col] = new Set([tileId]);
    }

    // Propagate initial constraints
    const constraintQueue: [number, number][] = constraints.map(({ row, col }) => [row, col]);
    if (!this.propagate(possible, constraintQueue, width, height)) {
      // If initial constraints create a contradiction, relax and fall back
      possible = Array.from({ length: height }, () =>
        Array.from({ length: width }, () => new Set(this.allIds))
      );
    }

    const backtrackStack: Snapshot[] = [];
    const MAX_BACKTRACKS = 1000;
    let backtracks = 0;

    while (true) {
      // Find lowest-entropy cell
      const cell = this.lowestEntropyCell(possible, width, height, rng);

      if (!cell) {
        // All cells are collapsed
        break;
      }

      const [row, col] = cell;
      const candidates = Array.from(possible[row][col]);

      if (candidates.length === 0) {
        // Contradiction — backtrack
        if (backtrackStack.length === 0 || backtracks >= MAX_BACKTRACKS) {
          // Total failure: fill with grass as a last resort
          return this.fallback(width, height);
        }
        backtracks++;

        const snap = backtrackStack.pop()!;
        possible = snap.possible;
        // Remove the previously chosen tile and try the next candidate
        possible[snap.row][snap.col] = new Set(snap.remaining);
        if (snap.remaining.length === 0) {
          // Exhausted all options — continue backtracking
          continue;
        }
        // Re-propagate from this cell
        if (!this.propagate(possible, [[snap.row, snap.col]], width, height)) {
          // Still contradiction — keep backtracking
          continue;
        }
        continue;
      }

      // Choose a tile weighted by weight
      const chosen = this.weightedChoice(candidates, rng);

      // Save snapshot before collapse (so we can backtrack)
      const snapshot: Snapshot = {
        row,
        col,
        possible: this.deepCopy(possible),
        chosen,
        remaining: candidates.filter((id) => id !== chosen),
      };
      backtrackStack.push(snapshot);

      // Collapse
      possible[row][col] = new Set([chosen]);

      // Propagate
      if (!this.propagate(possible, [[row, col]], width, height)) {
        // Contradiction: restore snapshot and try a different tile
        if (backtracks >= MAX_BACKTRACKS) {
          return this.fallback(width, height);
        }
        backtracks++;
        const snap = backtrackStack.pop()!;
        possible = snap.possible;
        possible[snap.row][snap.col] = new Set(snap.remaining);
        if (!this.propagate(possible, [[snap.row, snap.col]], width, height)) {
          continue;
        }
      }
    }

    // Build result grid
    const tiles: number[][] = [];
    for (let r = 0; r < height; r++) {
      const row: number[] = [];
      for (let c = 0; c < width; c++) {
        const ids = Array.from(possible[r][c]);
        const id = ids[0] ?? 'grass';
        const def = this.tileDefs.get(id);
        row.push(def ? def.index : 1);
      }
      tiles.push(row);
    }

    return { width, height, tiles };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Private helpers
  // ─────────────────────────────────────────────────────────────────────────

  /** Build the compatibility map from edge definitions. */
  private buildCompatibility(tileset: TilesetConfig): CompatibilityMap {
    const compat: CompatibilityMap = new Map();

    for (const tile of tileset.tiles) {
      const dirMap = new Map<Direction, Set<string>>();
      for (const dir of ['N', 'E', 'S', 'W'] as Direction[]) {
        dirMap.set(dir, new Set(tile.edges[dir] ?? []));
      }
      compat.set(tile.id, dirMap);
    }
    return compat;
  }

  /**
   * BFS constraint propagation.
   * Returns false if a contradiction (empty set) is detected.
   */
  private propagate(
    possible: Set<string>[][],
    queue: [number, number][],
    width: number,
    height: number
  ): boolean {
    const inQueue: boolean[][] = Array.from({ length: height }, () =>
      new Array(width).fill(false)
    );
    for (const [r, c] of queue) inQueue[r][c] = true;

    const q: [number, number][] = [...queue];

    while (q.length > 0) {
      const [r, c] = q.shift()!;
      inQueue[r][c] = false;

      const currentPossible = possible[r][c];
      if (currentPossible.size === 0) return false;

      for (const dir of ['N', 'E', 'S', 'W'] as Direction[]) {
        const [dr, dc] = DIR_DELTA[dir];
        const nr = r + dr;
        const nc = c + dc;
        if (nr < 0 || nr >= height || nc < 0 || nc >= width) continue;

        const opposite = OPPOSITE[dir];

        // Collect all tile ids that are reachable from (r,c) in direction dir
        const reachable = new Set<string>();
        for (const tileId of currentPossible) {
          const allowed = this.compat.get(tileId)?.get(dir);
          if (allowed) {
            for (const a of allowed) reachable.add(a);
          }
        }

        // Filter neighbour's possible set: keep only tiles that are reachable
        // from (r,c) AND that themselves allow (r,c)'s tile set in the opposite dir
        const neighbourPossible = possible[nr][nc];
        const before = neighbourPossible.size;

        for (const nId of Array.from(neighbourPossible)) {
          if (!reachable.has(nId)) {
            neighbourPossible.delete(nId);
          } else {
            // Also check the reverse: nId must allow at least one of currentPossible in opposite dir
            const nAllowed = this.compat.get(nId)?.get(opposite);
            if (!nAllowed) {
              neighbourPossible.delete(nId);
              continue;
            }
            let hasValid = false;
            for (const cId of currentPossible) {
              if (nAllowed.has(cId)) { hasValid = true; break; }
            }
            if (!hasValid) neighbourPossible.delete(nId);
          }
        }

        if (neighbourPossible.size === 0) return false;

        if (neighbourPossible.size < before && !inQueue[nr][nc]) {
          inQueue[nr][nc] = true;
          q.push([nr, nc]);
        }
      }
    }
    return true;
  }

  /**
   * Find the uncollapsed cell (size > 1) with the lowest Shannon entropy.
   * Returns null if all cells are collapsed.
   */
  private lowestEntropyCell(
    possible: Set<string>[][],
    width: number,
    height: number,
    rng: () => number
  ): [number, number] | null {
    let minEntropy = Infinity;
    let result: [number, number] | null = null;

    for (let r = 0; r < height; r++) {
      for (let c = 0; c < width; c++) {
        const size = possible[r][c].size;
        if (size <= 1) continue;

        // Shannon entropy with a small random noise to break ties
        const entropy = this.entropy(possible[r][c]) - rng() * 0.0001;
        if (entropy < minEntropy) {
          minEntropy = entropy;
          result = [r, c];
        }
      }
    }
    return result;
  }

  /** Shannon entropy of a cell's possible tile set (weighted). */
  private entropy(tileIds: Set<string>): number {
    let totalWeight = 0;
    for (const id of tileIds) {
      totalWeight += this.tileDefs.get(id)?.weight ?? 1;
    }
    let h = 0;
    for (const id of tileIds) {
      const w = (this.tileDefs.get(id)?.weight ?? 1) / totalWeight;
      if (w > 0) h -= w * Math.log2(w);
    }
    return h;
  }

  /** Pick a tile id randomly, weighted by tile.weight. */
  private weightedChoice(candidates: string[], rng: () => number): string {
    const weights = candidates.map((id) => this.tileDefs.get(id)?.weight ?? 1);
    const total = weights.reduce((a, b) => a + b, 0);
    let rand = rng() * total;
    for (let i = 0; i < candidates.length; i++) {
      rand -= weights[i];
      if (rand <= 0) return candidates[i];
    }
    return candidates[candidates.length - 1];
  }

  /** Deep copy the possible grid (for backtracking snapshots). */
  private deepCopy(possible: Set<string>[][]): Set<string>[][] {
    return possible.map((row) => row.map((cell) => new Set(cell)));
  }

  /** Fallback: fill all cells with grass. */
  private fallback(width: number, height: number): WFCResult {
    const grassDef = this.tileDefs.get('grass');
    const grassIndex = grassDef ? grassDef.index : 1;
    const wallDef = this.tileDefs.get('wall');
    const wallIndex = wallDef ? wallDef.index : 2;

    const tiles: number[][] = [];
    for (let r = 0; r < height; r++) {
      const row: number[] = [];
      for (let c = 0; c < width; c++) {
        const isBorder = r === 0 || r === height - 1 || c === 0 || c === width - 1;
        row.push(isBorder ? wallIndex : grassIndex);
      }
      tiles.push(row);
    }
    return { width, height, tiles };
  }
}
